import React from 'react';
import { useTranslation } from 'react-i18next';
import { Star } from 'lucide-react';

interface Review {
  id: string;
  name: string;
  date: string;
  rating: number;
  title: string;
  content: string;
  version: string;
  country: string;
}


const StarRating = ({ rating }: { rating: number }) => {
  return (
    <div className="flex gap-1">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`w-4 h-4 ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-400'}`}
        />
      ))}
    </div>
  );
};

const ReviewCard = ({ review, verifiedPurchaseText }: { review: Review; verifiedPurchaseText: string }) => {
  return (
    <div className="bg-navy-card rounded-xl p-6 border border-white/10 shadow-xl hover:shadow-2xl transition-all duration-300 hover:border-white/20">
      <span className="text-6xl text-primary/30 font-serif leading-none block mb-2">"</span>
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <StarRating rating={review.rating} />
          <h3 className="text-lg font-semibold text-white mt-3 mb-2">{review.title}</h3>
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <span>{review.name}</span>
            <span>•</span>
            <span>{review.date}</span>
          </div>
        </div>
      </div>

      <p className="text-gray-300 mb-4 leading-relaxed italic">"{review.content}"</p>

      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>{review.version}</span>
        <div className="flex items-center gap-1">
          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
          <span>{verifiedPurchaseText}</span>
        </div>
      </div>
    </div>
  );
};

const Reviews = () => {
  const { t } = useTranslation();
  
  const reviewsData = Array.isArray(t('reviews.reviewsData', { returnObjects: true })) 
    ? (t('reviews.reviewsData', { returnObjects: true }) as any[]).map((review, index) => ({
        ...review,
        id: review.id || (index + 1).toString(),
        rating: review.rating || 5
      }))
    : [
        {
          id: '1',
          name: 'Cornelia Camelia',
          date: 'Jul 27, 2025',
          rating: 5,
          title: 'Really great transcription app!!',
          content: 'Notely really helps me become more efficient at work. Writes really good notes and can easily summarize and chat.',
          version: 'Version 1.3.3',
          country: 'Sweden'
        },
        {
          id: '2',
          name: 'Jacob Joon',
          date: 'Jun 21, 2025',
          rating: 5,
          title: 'This is such an amazing app',
          content: 'I have been using this for client meetings. The transcription is spot-on and the smart summarisation saves me time. Worth trying out!',
          version: 'Version 1.2.7',
          country: 'United States'
        },
        {
          id: '3',
          name: 'Helga F. S.',
          date: 'Mar 5, 2025',
          rating: 5,
          title: 'So efficient!',
          content: 'Finally I can be more present in my meetings while Notely AI is taking the notes and summarizes actions/key points for me. I can highly recommend!',
          version: 'Version 1.1.0',
          country: 'Sweden'
        }
      ];
  
  return (
    <section className="py-24 px-4 bg-navy">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-primary/20 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <span className="w-2 h-2 bg-primary rounded-full animate-pulse"></span>
            {t('reviews.badge')}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t('reviews.title')}
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {t('reviews.subtitle')}
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {reviewsData.map((review, index) => (
            <div 
              key={review.id || index} 
              className="animate-slide-up opacity-0"
              style={{ animationDelay: `${0.1 * (index + 1)}s` }}
            >
              <ReviewCard review={review} verifiedPurchaseText={t('reviews.verifiedPurchase')} />
            </div>
          ))}
        </div>
        
        <div className="text-center">
          <div className="inline-flex items-center gap-3 bg-yellow-400/10 px-6 py-4 rounded-2xl">
            <StarRating rating={5} />
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-400">4.9/5</div>
              <div className="text-sm text-gray-400">{t('reviews.appStoreRating')}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Reviews;